import { Tag } from 'antd';
import { startCase as _startCase } from 'lodash';
import { useCallback } from 'react';
import { useTheme } from 'styled-components';

import {
  AtlasGqlInspectionStatus,
  type AtlasGqlInspectionDeliveryStatus,
  type AtlasGqlTaskStatus,
} from 'types/atlas-graphql';
import { type HorizonTheme } from 'utils/color/horizonTheme';

export function getSeverityColor(
  theme: HorizonTheme,
  severity?: number | null,
  maxValue: number = 5
) {
  if (!severity || severity > maxValue) {
    return null;
  }
  if (severity === maxValue) {
    return theme.severityMaxColor;
  }

  if (severity >= maxValue - 1) {
    return theme.severityHighColor;
  }
}

export const StatusTag: React.FC<{
  status: AtlasGqlTaskStatus | AtlasGqlInspectionStatus | AtlasGqlInspectionDeliveryStatus;
  startCase?: boolean;
}> = ({ status, startCase = true }) => {
  const theme = useTheme();

  const getStatusColor = useCallback(
    (status: AtlasGqlTaskStatus | AtlasGqlInspectionStatus | AtlasGqlInspectionDeliveryStatus) => {
      switch (status) {
        case 'IN_PROGRESS':
        case AtlasGqlInspectionStatus.AnalysisPending:
        case AtlasGqlInspectionStatus.AnalysisInProgress:
          return theme.teal;
        case 'PENDING':
        case 'DRAFT':
        case AtlasGqlInspectionStatus.DataCapturePending:
        case AtlasGqlInspectionStatus.DataCaptureInProgress:
          return theme.defaultStatusColor;
        case 'COMPLETE':
        case 'COMPLETED':
        case 'RELEASED':
        case AtlasGqlInspectionStatus.AnalysisComplete:
        case AtlasGqlInspectionStatus.Delivered:
          return theme.green;
        case 'FAILED':
          return theme.red;
        default:
          return undefined;
      }
    },
    [status, theme]
  );

  return (
    <Tag color={getStatusColor(status)}>
      {startCase ? _startCase(status.toLowerCase()) : status}
    </Tag>
  );
};

export type AssetColorStatus =
  | 'Active'
  | 'Curtailed'
  | 'Decommissioned'
  | 'Offline'
  | 'Online'
  | 'Recycled'
  | 'Storage';

export function getAssetColorStatus(status: AssetColorStatus, theme: HorizonTheme) {
  switch (status) {
    case 'Offline':
    case 'Recycled':
      return theme.red;
    case 'Online':
    case 'Active':
      return theme.teal;
    case 'Curtailed':
    case 'Storage':
      return theme.severityHighColor;
    case 'Decommissioned':
      return theme.secondaryBackgroundColor;
    default:
      return theme.secondaryBackgroundColor;
  }
}
